/**
 * VendorProductEventLimitService
 * Service for managing vendor product event limits (composite key)
 */
import { BaseService } from '../base/BaseService';
/**
 * Service for vendor product event limit operations with composite key (productId + eventId)
 */
export class VendorProductEventLimitService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? VendorProductEventLimitService.DEFAULT_SERVICE_PATH,
            basePath: VendorProductEventLimitService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET ALL ====================
    /**
     * Get all vendor product event limits
     * @returns Array of vendor product event limits
     */
    async getAll() {
        const response = await this.getAllRaw();
        return response.data ?? [];
    }
    /**
     * Get all vendor product event limits with full response
     * @returns ResponseData with array of vendor product event limits
     */
    getAllRaw() {
        const url = this.buildUrl();
        return this.http.get(url);
    }
    // ==================== GET BY COMPOSITE ID ====================
    /**
     * Get vendor product event limit by composite key
     * @param productId - Product ID
     * @param eventId - Event ID
     * @returns The vendor product event limit
     */
    async getById(productId, eventId) {
        const response = await this.getByIdRaw(productId, eventId);
        if (!response.data) {
            throw new Error(`VendorProductEventLimit not found: productId=${productId}, eventId=${eventId}`);
        }
        return response.data;
    }
    /**
     * Get vendor product event limit by composite key with full response
     * @param productId - Product ID
     * @param eventId - Event ID
     * @returns ResponseData with vendor product event limit
     */
    getByIdRaw(productId, eventId) {
        const url = this.buildUrl(`${productId}/${eventId}`);
        return this.http.get(url);
    }
    // ==================== CREATE ====================
    /**
     * Create a new vendor product event limit
     * @param data - Vendor product event limit data
     * @returns The created vendor product event limit
     */
    async create(data) {
        const response = await this.createRaw(data);
        if (!response.data) {
            throw new Error('Failed to create VendorProductEventLimit');
        }
        return response.data;
    }
    /**
     * Create a new vendor product event limit with full response
     * @param data - Vendor product event limit data
     * @returns ResponseData with created vendor product event limit
     */
    createRaw(data) {
        const url = this.buildUrl();
        return this.http.post(url, data);
    }
    // ==================== UPDATE ====================
    /**
     * Update a vendor product event limit
     * @param productId - Product ID
     * @param eventId - Event ID
     * @param data - Updated vendor product event limit data
     * @returns The updated vendor product event limit
     */
    async update(productId, eventId, data) {
        const response = await this.updateRaw(productId, eventId, data);
        if (!response.data) {
            throw new Error(`Failed to update VendorProductEventLimit: productId=${productId}, eventId=${eventId}`);
        }
        return response.data;
    }
    /**
     * Update a vendor product event limit with full response
     * @param productId - Product ID
     * @param eventId - Event ID
     * @param data - Updated vendor product event limit data
     * @returns ResponseData with updated vendor product event limit
     */
    updateRaw(productId, eventId, data) {
        const url = this.buildUrl(`${productId}/${eventId}`);
        return this.http.put(url, data);
    }
    // ==================== DELETE ====================
    /**
     * Delete a vendor product event limit
     * @param productId - Product ID
     * @param eventId - Event ID
     * @returns True if deleted successfully
     */
    async delete(productId, eventId) {
        const response = await this.deleteRaw(productId, eventId);
        return response.success;
    }
    /**
     * Delete a vendor product event limit with full response
     * @param productId - Product ID
     * @param eventId - Event ID
     * @returns ResponseData (success/fail)
     */
    deleteRaw(productId, eventId) {
        const url = this.buildUrl(`${productId}/${eventId}`);
        return this.http.delete(url);
    }
    // ==================== CUSTOM QUERIES ====================
    /**
     * Get all event limits for a specific product
     * @param productId - Product ID
     * @returns Array of vendor product event limit views
     */
    async getByProduct(productId) {
        const response = await this.getByProductRaw(productId);
        return response.data ?? [];
    }
    /**
     * Get all event limits for a specific product with full response
     * @param productId - Product ID
     * @returns ResponseData with array of vendor product event limit views
     */
    getByProductRaw(productId) {
        const url = this.buildUrl(`product/${productId}`);
        return this.http.get(url);
    }
    /**
     * Get all product limits for a specific event
     * @param eventId - Event ID
     * @returns Array of vendor product event limit views
     */
    async getByEvent(eventId) {
        const response = await this.getByEventRaw(eventId);
        return response.data ?? [];
    }
    /**
     * Get all product limits for a specific event with full response
     * @param eventId - Event ID
     * @returns ResponseData with array of vendor product event limit views
     */
    getByEventRaw(eventId) {
        const url = this.buildUrl(`event/${eventId}`);
        return this.http.get(url);
    }
}
VendorProductEventLimitService.DEFAULT_SERVICE_PATH = 'vendor-catalogs';
VendorProductEventLimitService.BASE_PATH = 'api/vendor-product-event-limits';
//# sourceMappingURL=VendorProductEventLimitService.js.map